import { ActionTree } from 'vuex'

import sanitizePhoneNumber from '@/helpers/sanitizePhoneNumber'
import TagManager from '@/sdk/careshipTracking/src/gtm'
import { getContract, getServiceRequest } from '@/sdk/clients/ServicesRequest'
import {
  postAcceptTermsAndConditions,
  emailVerificationStatus,
  getIsPasswordSet,
  getUserDetails,
  setPassword,
  updateUserDetails,
  verifyEmailAddress,
} from '@/sdk/clients/UserClient'
import RootState from '@/store/types/RootState'

import {
  Contract,
  Mutations,
  ServiceRequest,
  UserDetails,
  UserState,
} from './types'

const actions: ActionTree<UserState, RootState> = {
  async getUserDetails({ commit }): Promise<UserDetails> {
    const userDetails: UserDetails = await getUserDetails()
    commit(Mutations.SET_USER_DETAILS, userDetails)

    // TODO: Is this userId used for anything?
    // GTM user id
    TagManager.getDataLayer().push({
      userId: userDetails.customer_id,
    })

    return userDetails
  },

  async updateUserDetails(
    { commit, getters: { userDetails } },
    {
      firstName,
      lastName,
      phoneNumber,
    }: {
      firstName: string
      lastName: string
      phoneNumber: string
    },
  ) {
    const updatedUserDetails = {
      ...userDetails,
      name: {
        first_name: firstName,
        last_name: lastName,
      },
      phone_number: sanitizePhoneNumber(phoneNumber),
    }

    await updateUserDetails(updatedUserDetails)
    commit(Mutations.SET_USER_DETAILS, updatedUserDetails)
  },

  async updatePhoneNumber(
    { commit, getters: { userDetails } },
    phoneNumber: string,
  ) {
    const updatedUserDetails = {
      ...userDetails,
      phone_number: sanitizePhoneNumber(phoneNumber),
    }

    await updateUserDetails(updatedUserDetails)
    commit(Mutations.SET_USER_DETAILS, updatedUserDetails)
  },

  async updateNameAndEmail(
    { commit, getters: { userDetails } },
    {
      email,
      firstName,
      lastName,
    }: {
      email: string
      firstName: string
      lastName: string
    },
  ) {
    const updatedUserDetails: UserDetails = {
      ...userDetails,
      email,
      name: {
        first_name: firstName,
        last_name: lastName,
      },
    }

    await updateUserDetails(updatedUserDetails)
    commit(Mutations.SET_USER_DETAILS, updatedUserDetails)
  },

  async updateEmail({ commit, getters: { userDetails } }, email: string) {
    const updatedUserDetails = {
      ...userDetails,
      email,
    }

    await updateUserDetails(updatedUserDetails)
    commit(Mutations.SET_USER_DETAILS, updatedUserDetails)
  },

  async emailVerificationStatus({ commit }): Promise<boolean> {
    const { is_verified } = await emailVerificationStatus()
    commit(Mutations.SET_EMAIL_PENDING_VERIFICATION, is_verified)

    return is_verified
  },

  async verifyEmailAddress(_, customerId: string) {
    return verifyEmailAddress(customerId)
  },

  // TODO: Move serviceRequest away from the user module
  async getServiceRequest({ commit }, serviceRequestId: string) {
    const serviceRequest: ServiceRequest = await getServiceRequest(
      serviceRequestId,
    )
    commit(Mutations.SET_SERVICE_REQUEST, serviceRequest)
  },

  // TODO: Move contract away from the user module
  async getContract({ commit }, contractId: string) {
    const contract: Contract = await getContract(contractId)
    commit(Mutations.SET_CONTRACT, contract)
  },

  async setPassword(_, password: string) {
    return setPassword({ password })
  },

  async fetchIsPasswordSet({ commit, getters: { customerId } }) {
    const isPasswordSet: boolean = await getIsPasswordSet(customerId)
    commit(Mutations.SET_IS_PASSWORD_SET, isPasswordSet)
  },

  async fetchDashboardInitialData({ dispatch }) {
    const isEmailVerified = await dispatch('emailVerificationStatus')

    if (!isEmailVerified) {
      return
    }

    await dispatch('getUserDetails')
    await dispatch('fetchIsPasswordSet')
  },

  setIsOneTimeLoginSession({ commit }) {
    commit(Mutations.SET_IS_ONE_TIME_LOGIN_SESSION, true)
  },

  async acceptTermsAndConditions({ getters: { customerId } }) {
    await postAcceptTermsAndConditions(customerId)
  },
}

export default actions
